/**
 * Agents.js
 * Page showing available AI agents
 */

import React from "react";
import { Link } from "react-router-dom";
import { useTeam } from "../context/TeamContext";
import { FaProjectDiagram, FaLock } from "react-icons/fa";

export default function Agents() {
  const { permissions } = useTeam();

  const agents = [
    {
      id: "flowchart",
      name: "Flowchart Generator",
      description:
        "Generate professional Mermaid flowcharts from your context documents. Creates process flows, decision trees, and system diagrams.",
      icon: "📊",
      available: true,
      path: "/data-next/agents/flowchart",
    },
    {
      id: "er-diagram",
      name: "ER Diagram Generator",
      description:
        "Create entity-relationship diagrams from your database documentation or requirements.",
      icon: "🗄️",
      available: false,
      comingSoon: true,
    },
    {
      id: "timeline",
      name: "Timeline Generator",
      description:
        "Build Gantt charts and timelines from project plans and schedules.",
      icon: "📅",
      available: false,
      comingSoon: true,
    },
    {
      id: "mindmap",
      name: "Mind Map Generator",
      description: "Transform brainstorming notes into structured mind maps.",
      icon: "🧠",
      available: false,
      comingSoon: true,
    },
    {
      id: "architecture",
      name: "Architecture Diagram Generator",
      description:
        "Create system architecture diagrams from technical specifications.",
      icon: "🏗️",
      available: false,
      comingSoon: true,
    },
    {
      id: "sequence",
      name: "Sequence Diagram Generator",
      description:
        "Generate UML sequence diagrams from API documentation or interaction flows.",
      icon: "🔄",
      available: false,
      comingSoon: true,
    },
  ];

  if (!permissions.canWrite) {
    return (
      <div className="agents-page">
        <div className="page-header">
          <h1>Agents</h1>
          <p className="page-subtitle">AI-powered diagram generators</p>
        </div>

        <div className="access-denied-message">
          <FaLock className="lock-icon" />
          <h2>Creator Access Required</h2>
          <p>You need creator or owner permissions to use agents.</p>
          <p className="hint">
            Contact your team owner to upgrade your access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="agents-page">
      {/* Header */}
      <div className="page-header">
        <h1>Agents</h1>
        <p className="page-subtitle">
          AI-powered diagram generators for your context
        </p>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <div className="info-icon">💡</div>
        <div className="info-content">
          <h3>How Agents Work</h3>
          <p>
            Select an agent, choose context documents from your library,
            describe what you want, and let AI generate professional diagrams.
            You can edit and refine the results.
          </p>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="agents-grid">
        {agents.map((agent) => (
          <div
            key={agent.id}
            className={`agent-card ${
              agent.available ? "available" : "unavailable"
            }`}
          >
            <div className="agent-icon">{agent.icon}</div>
            <div className="agent-content">
              <h3 className="agent-name">
                {agent.name}
                {agent.comingSoon && (
                  <span className="coming-soon-badge">Coming Soon</span>
                )}
              </h3>
              <p className="agent-description">{agent.description}</p>
            </div>
            <div className="agent-footer">
              {agent.available ? (
                <Link to={agent.path} className="btn btn-primary">
                  <FaProjectDiagram /> Launch Agent
                </Link>
              ) : (
                <button disabled className="btn btn-secondary">
                  Not Available Yet
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Agent Capabilities */}
      <div className="capabilities-section">
        <h2>Agent Capabilities</h2>
        <div className="capabilities-grid">
          <div className="capability-card">
            <div className="capability-icon">🎯</div>
            <h4>Context-Aware</h4>
            <p>
              Agents analyze your uploaded documents to understand the domain
            </p>
          </div>
          <div className="capability-card">
            <div className="capability-icon">✨</div>
            <h4>AI-Powered</h4>
            <p>Leverages Google Vertex AI for intelligent diagram generation</p>
          </div>
          <div className="capability-card">
            <div className="capability-icon">✏️</div>
            <h4>Editable Results</h4>
            <p>Edit generated diagrams with live preview and version history</p>
          </div>
          <div className="capability-card">
            <div className="capability-icon">💾</div>
            <h4>Multiple Formats</h4>
            <p>Download as SVG, PNG, or Mermaid source code</p>
          </div>
        </div>
      </div>
    </div>
  );
}
